import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import {
  User,
  CheckCircle,
  Clock,
  Target,
  UserPlus,
  FileText,
  TrendingUp,
} from 'lucide-react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  ResponsiveContainer,
  Tooltip,
} from 'recharts';
import type { TaskWithRelations, UserWithTeam } from '@shared/schema';

interface StaffPerformanceReportProps {
  user: UserWithTeam;
}

export default function StaffPerformanceReport({
  user,
}: StaffPerformanceReportProps) {
  // Fetch all tasks for global metrics
  const { data: allTasks = [] } = useQuery<TaskWithRelations[]>({
    queryKey: ['/api/tasks'],
    retry: false,
  });

  // Fetch user-specific tasks including help tasks
  const { data: userTasks = [] } = useQuery<TaskWithRelations[]>({
    queryKey: ['/api/users', user.id, 'tasks'],
    retry: false,
  });

  // Calculate performance metrics and trend data
  const { performanceMetrics, trendData } = useMemo(() => {
    const now = new Date();

    // User's assigned tasks (exclude help tasks for main metrics)
    const assignedTasks = userTasks.filter(task => task.assignedTo === user.id);
    const completedTasks = assignedTasks.filter(
      task => task.status === 'complete'
    );

    // Deadline performance
    const tasksCompletedBeforeDeadline = completedTasks.filter(task => {
      if (!task.completedAt) return false;
      const completedDate = new Date(task.completedAt);
      const dueDate = new Date(task.dueDate);
      return completedDate <= dueDate;
    });

    // Completion percentage
    const completionPercentage =
      assignedTasks.length > 0
        ? Math.round((completedTasks.length / assignedTasks.length) * 100)
        : 0;

    // Self-assignment metrics
    const unassignedTasks = allTasks.filter(task => !task.assignedTo);
    const selfAssignedTasks = allTasks.filter(
      task => task.assignedTo === user.id && task.createdBy !== user.id
    );

    // Generate trend data for the last 7 days
    const trendData = [];
    for (let i = 6; i >= 0; i--) {
      const date = new Date();
      date.setDate(date.getDate() - i);
      const dateStr = date.toISOString().split('T')[0];

      // Count tasks completed on this date
      const completedOnDate = completedTasks.filter(task => {
        if (!task.completedAt) return false;
        const completedDate = new Date(task.completedAt);
        return completedDate.toISOString().split('T')[0] === dateStr;
      }).length;

      // Count tasks assigned on this date
      const assignedOnDate = assignedTasks.filter(task => {
        const createdDate = new Date(task.createdAt);
        return createdDate.toISOString().split('T')[0] === dateStr;
      }).length;

      trendData.push({
        date: date.toLocaleDateString('en-US', {
          month: 'short',
          day: 'numeric',
        }),
        completed: completedOnDate,
        assigned: assignedOnDate,
      });
    }

    return {
      performanceMetrics: {
        totalAssigned: assignedTasks.length,
        totalCompleted: completedTasks.length,
        completionPercentage,
        completedBeforeDeadline: tasksCompletedBeforeDeadline.length,
        deadlinePerformance:
          completedTasks.length > 0
            ? Math.round(
                (tasksCompletedBeforeDeadline.length / completedTasks.length) *
                  100
              )
            : 0,
        unassignedTasksAvailable: unassignedTasks.length,
        selfAssignedCount: selfAssignedTasks.length,
      },
      trendData,
    };
  }, [userTasks, allTasks, user.id]);

  return (
    <Card className="w-full">
      <CardHeader className="pb-3">
        <CardTitle className="text-lg flex items-center gap-2">
          <User className="w-5 h-5" />
          Performance Report
        </CardTitle>
        <div className="text-sm text-gray-600">
          {user.firstName} {user.lastName}
        </div>
      </CardHeader>

      <CardContent className="space-y-4">
        {/* Task Assignment Overview */}
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <FileText className="w-4 h-4 text-blue-500" />
              <span className="text-sm font-medium">Tasks Assigned</span>
            </div>
            <Badge variant="secondary">
              {performanceMetrics.totalAssigned}
            </Badge>
          </div>

          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <CheckCircle className="w-4 h-4 text-green-500" />
              <span className="text-sm font-medium">Completed</span>
            </div>
            <Badge variant="secondary">
              {performanceMetrics.totalCompleted}
            </Badge>
          </div>
        </div>

        {/* Completion Percentage */}
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium">Completion Rate</span>
            <span className="text-sm font-bold text-green-600">
              {performanceMetrics.completionPercentage}%
            </span>
          </div>
          <Progress
            value={performanceMetrics.completionPercentage}
            className="h-2"
          />
        </div>

        {/* Deadline Performance */}
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Target className="w-4 h-4 text-orange-500" />
              <span className="text-sm font-medium">On-Time Completion</span>
            </div>
            <Badge variant="outline">
              {performanceMetrics.completedBeforeDeadline}/
              {performanceMetrics.totalCompleted}
            </Badge>
          </div>

          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium">Deadline Performance</span>
              <span className="text-sm font-bold text-orange-600">
                {performanceMetrics.deadlinePerformance}%
              </span>
            </div>
            <Progress
              value={performanceMetrics.deadlinePerformance}
              className="h-2"
            />
          </div>
        </div>

        {/* Self-Assignment Activity */}
        <div className="space-y-3 pt-3 border-t border-gray-200">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Clock className="w-4 h-4 text-purple-500" />
              <span className="text-sm font-medium">Unassigned Tasks</span>
            </div>
            <Badge variant="outline">
              {performanceMetrics.unassignedTasksAvailable}
            </Badge>
          </div>

          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <UserPlus className="w-4 h-4 text-blue-500" />
              <span className="text-sm font-medium">Self-Assigned</span>
            </div>
            <Badge variant="secondary">
              {performanceMetrics.selfAssignedCount}
            </Badge>
          </div>
        </div>

        {/* Performance Trend Graph */}
        <div className="pt-3 border-t border-gray-200">
          <div className="flex items-center gap-2 mb-3">
            <TrendingUp className="w-4 h-4 text-blue-500" />
            <span className="text-sm font-medium">7-Day Trend</span>
          </div>

          <div className="h-24 w-full">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={trendData}>
                <XAxis
                  dataKey="date"
                  axisLine={false}
                  tickLine={false}
                  tick={{ fontSize: 10 }}
                />
                <YAxis hide />
                <Tooltip
                  labelStyle={{ fontSize: '12px' }}
                  contentStyle={{ fontSize: '12px', padding: '4px 8px' }}
                />
                <Line
                  type="monotone"
                  dataKey="completed"
                  stroke="#10b981"
                  strokeWidth={2}
                  dot={{ r: 2 }}
                  name="Completed"
                />
                <Line
                  type="monotone"
                  dataKey="assigned"
                  stroke="#3b82f6"
                  strokeWidth={2}
                  dot={{ r: 2 }}
                  name="Assigned"
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Performance Indicators */}
        <div className="pt-3 border-t border-gray-200">
          <div className="grid grid-cols-2 gap-2">
            <div
              className={`p-2 rounded text-center ${
                performanceMetrics.completionPercentage >= 80
                  ? 'bg-green-50 text-green-700'
                  : performanceMetrics.completionPercentage >= 60
                    ? 'bg-yellow-50 text-yellow-700'
                    : 'bg-red-50 text-red-700'
              }`}
            >
              <div className="text-xs font-medium">Overall</div>
              <div className="text-xs">
                {performanceMetrics.completionPercentage >= 80
                  ? 'Excellent'
                  : performanceMetrics.completionPercentage >= 60
                    ? 'Good'
                    : 'Needs Focus'}
              </div>
            </div>

            <div
              className={`p-2 rounded text-center ${
                performanceMetrics.deadlinePerformance >= 80
                  ? 'bg-green-50 text-green-700'
                  : performanceMetrics.deadlinePerformance >= 60
                    ? 'bg-yellow-50 text-yellow-700'
                    : 'bg-red-50 text-red-700'
              }`}
            >
              <div className="text-xs font-medium">Timeliness</div>
              <div className="text-xs">
                {performanceMetrics.deadlinePerformance >= 80
                  ? 'On Track'
                  : performanceMetrics.deadlinePerformance >= 60
                    ? 'Improving'
                    : 'Delayed'}
              </div>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
